import React, { useMemo, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { gql, useQuery, useMutation } from '@apollo/client';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { useCan } from '@feedflo/access-control';
import Button, { Colors } from '../../atoms/Button';
import ConfirmationDialog from '../../organisms/ConfirmationDialog';
import Combobox from '../../atoms/Combobox';
import { DEVICE_GQL } from './queries';

const shortenAlgorithmVersion = (version) => {
  if (!version) return version;
  return version.length > 8 ? `${version.substring(0, 8)}...` : version;
};

const INSERT_DEVICE_ALGORITHM_OVERRIDE_MUTATION = gql`
  mutation InsertDeviceAlgorithmOverride(
    $device_id: uuid!
    $algorithm_version: String!
  ) {
    insert_device_algorithm_override(
      objects: { device_id: $device_id, algorithm_version: $algorithm_version }
    ) {
      affected_rows
    }
  }
`;

const DELETE_DEVICE_ALGORITHM_OVERRIDE_MUTATION = gql`
  mutation DeleteDeviceAlgorithmOverride(
    $device_id: uuid!
    $deleted_at: bigint!
  ) {
    update_device_algorithm_override(
      where: { device_id: { _eq: $device_id }, deleted_at: { _is_null: true } }
      _set: { deleted_at: $deleted_at }
    ) {
      affected_rows
    }
  }
`;

const GET_ALGORITHM_VERSIONS_GQL = gql`
  query AlgorithmVersionsQuery {
    algorithm(where: { deleted_at: { _is_null: true } }, order_by: { created_at: desc }) {
      version
      description
      published_at
    }
  }
`;

export default function DeviceAlgorithmOverride({ deviceId, deviceAlgorithmOverride }) {
  const { canAnywhere } = useCan();
  const hasStaffAccess = canAnywhere('feature', 'feature:staff_access');
  const confirmationDialogRef = useRef(null);
  const [confirmationCallback, setConfirmationCallback] = useState(null);
  const [confirmationMessage, setConfirmationMessage] = useState('');
  const [selectedAlgorithmVersion, setSelectedAlgorithmVersion] = useState(null);

  // Initialize selectedAlgorithmVersion when device data loads
  useEffect(() => {
    if (deviceAlgorithmOverride?.algorithm_version) {
      setSelectedAlgorithmVersion(deviceAlgorithmOverride.algorithm_version);
    } else if (!deviceAlgorithmOverride && selectedAlgorithmVersion === null) {
      setSelectedAlgorithmVersion(null);
    }
  }, [deviceAlgorithmOverride?.algorithm_version]);

  const {
    loading: algorithmVersionsLoading,
    data: algorithmVersionsData,
  } = useQuery(GET_ALGORITHM_VERSIONS_GQL, {
    skip: !hasStaffAccess,
    context: {
      headers: {
        'x-hasura-role': 'user',
      },
    },
  });

  const [insertDeviceOverride, { loading: insertOverrideLoading }] = useMutation(INSERT_DEVICE_ALGORITHM_OVERRIDE_MUTATION, {
    context: {
      headers: {
        'x-hasura-role': 'user',
      },
    },
    refetchQueries: [DEVICE_GQL],
    onCompleted: () => {
      toast('Algorithm override updated', {
        position: 'top-right',
        autoClose: 3000,
        closeOnClick: true,
      });
      setSelectedAlgorithmVersion(null);
    },
    onError: (error) => {
      console.error(error);
      toast(`Error: ${error.message}`, {
        type: 'error',
        position: 'top-right',
        autoClose: 3000,
        closeOnClick: true,
      });
    },
  });

  const [deleteDeviceOverride, { loading: deleteOverrideLoading }] = useMutation(DELETE_DEVICE_ALGORITHM_OVERRIDE_MUTATION, {
    context: {
      headers: {
        'x-hasura-role': 'user',
      },
    },
    refetchQueries: [DEVICE_GQL],
    onCompleted: () => {
      setSelectedAlgorithmVersion(null);
    },
    onError: (error) => {
      console.error(error);
      toast(`Error: ${error.message}`, {
        type: 'error',
        position: 'top-right',
        autoClose: 3000,
        closeOnClick: true,
      });
    },
  });

  const overrideMutationLoading = insertOverrideLoading || deleteOverrideLoading;

  const showConfirmationDialog = (message, onConfirm) => {
    setConfirmationMessage(message);
    setConfirmationCallback(() => onConfirm);
    if (confirmationDialogRef?.current) {
      confirmationDialogRef.current.showModal();
    }
  };

  const handleConfirmDelete = () => {
    if (confirmationCallback) {
      confirmationCallback();
    }
    setConfirmationCallback(null);
    setConfirmationMessage('');
  };

  const handleCancelDelete = () => {
    setConfirmationCallback(null);
    setConfirmationMessage('');
  };

  const handleSaveOverride = async () => {
    const unixNow = dayjs().unix();

    try {
      // If there's a current override, delete it first
      if (deviceAlgorithmOverride) {
        await deleteDeviceOverride({
          variables: {
            device_id: deviceId,
            deleted_at: unixNow,
          },
          refetchQueries: [DEVICE_GQL],
        });
      }

      // Then insert the new override
      if (effectiveVersion) {
        await insertDeviceOverride({
          variables: {
            device_id: deviceId,
            algorithm_version: effectiveVersion,
          },
          refetchQueries: [DEVICE_GQL],
        });
      }
    } catch (error) {
      // Error handling is done in the mutation onError callbacks
      console.error('Error saving override:', error);
    }
  };

  const handleClearOverride = () => {
    showConfirmationDialog(
      'Are you sure you want to clear the algorithm override? The device will use the default (latest published) algorithm version.',
      async () => {
        setSelectedAlgorithmVersion(null);
        const unixNow = dayjs().unix();
        try {
          await deleteDeviceOverride({
            variables: {
              device_id: deviceId,
              deleted_at: unixNow,
            },
          });
          toast('Algorithm override cleared', {
            position: 'top-right',
            autoClose: 3000,
            closeOnClick: true,
          });
        } catch (error) {
          // Error handling is done in the mutation onError callbacks
          console.error('Error clearing override:', error);
        }
      },
    );
  };

  const currentOverrideVersion = deviceAlgorithmOverride?.algorithm_version || null;
  const effectiveVersion = useMemo(() => {
    if (selectedAlgorithmVersion !== null && selectedAlgorithmVersion !== undefined) {
      return selectedAlgorithmVersion;
    }
    return currentOverrideVersion;
  }, [selectedAlgorithmVersion, currentOverrideVersion]);

  // Don't render if user doesn't have staff access
  if (!hasStaffAccess) {
    return null;
  }

  return (
    <>
      <ConfirmationDialog
        dialogRef={confirmationDialogRef}
        message={confirmationMessage}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        confirmColor={Colors.Danger}
      />
      <div className="DeviceCard-algorithmOverride">
        {algorithmVersionsLoading ? (
          <div>Loading algorithm versions...</div>
        ) : (
          <>
            {deviceAlgorithmOverride && (
              <div className="DeviceCard-algorithmOverride-info">
                <div>
                  <strong>Current Override:</strong> {shortenAlgorithmVersion(deviceAlgorithmOverride.algorithm_version)}
                  {deviceAlgorithmOverride.algorithm?.description &&
                    ` - ${deviceAlgorithmOverride.algorithm.description}`}
                  {deviceAlgorithmOverride.algorithm?.published_at ? ' (Published)' : ' (Unpublished)'}
                </div>
              </div>
            )}
            <div className="DeviceCard-algorithmOverride-controls">
              <Combobox
                label="Algorithm Version Override"
                placeholder="No Override (use default)"
                value={effectiveVersion || ''}
                disabled={overrideMutationLoading}
                items={[
                  { id: '', name: 'No Override (use default)' },
                  ...(algorithmVersionsData?.algorithm?.map((alg) => ({
                    id: alg.version,
                    name: `${shortenAlgorithmVersion(alg.version)}${alg.description ? ` - ${alg.description}` : ''}${alg.published_at ? ' (Published)' : ' (Unpublished)'
                      }`,
                  })) || []),
                ]}
                onChange={(selectedItem) => {
                  setSelectedAlgorithmVersion(selectedItem?.id === '' ? null : selectedItem?.id);
                }}
              />
              {(effectiveVersion || currentOverrideVersion) && (
                <Button
                  color="error"
                  variant="text"
                  content="Clear Override"
                  onClick={handleClearOverride}
                  disabled={overrideMutationLoading}
                />
              )}
              {selectedAlgorithmVersion !== null &&
                selectedAlgorithmVersion !== currentOverrideVersion && (
                  <Button
                    color="success"
                    variant="vivid"
                    content={overrideMutationLoading ? 'Saving...' : 'Save Override'}
                    onClick={handleSaveOverride}
                    disabled={overrideMutationLoading}
                  />
                )}
            </div>
          </>
        )}
      </div>
    </>
  );
}

DeviceAlgorithmOverride.propTypes = {
  deviceId: PropTypes.string.isRequired,
  deviceAlgorithmOverride: PropTypes.shape({
    id: PropTypes.string,
    algorithm_version: PropTypes.string,
    algorithm: PropTypes.shape({
      version: PropTypes.string,
      description: PropTypes.string,
      published_at: PropTypes.number,
    }),
  }),
};
